if (!customElements.get("labor-day-2026-mix-match-cart")) {
  class LaborDayMixMatchCart extends HTMLElement {
    constructor() {
      super();
      this.items = new Map();
      this.productCache = new Map();
      this.lastFocusedElement = null;
      this.isSubmitting = false;
      this.visibilityFrame = null;
      this.onDocumentClick = this.onDocumentClick.bind(this);
      this.onSectionClick = this.onSectionClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onCartSuccess = this.onCartSuccess.bind(this);
      this.onCartError = this.onCartError.bind(this);
      this.onViewportChange = this.onViewportChange.bind(this);
    }

    connectedCallback() {
      this.discountRules = this.parseDiscountRules(this.dataset.discountRules);
      this.modal = this.querySelector("[data-modal]");
      this.dialog = this.querySelector("[data-dialog]");
      this.addComponent = this.querySelector(".labor-day-mix-match__add");
      this.addButton = this.querySelector("[data-add-button]");
      this.baseAddLabel = this.querySelector("[data-add-label]")?.textContent.trim() || "Add to cart";
      this.visibilityStart = this.findVisibilityBoundary(this.dataset.visibilityStart);
      this.visibilityEnd = this.findVisibilityBoundary(this.dataset.visibilityEnd);

      document.addEventListener("click", this.onDocumentClick, true);
      document.addEventListener("keydown", this.onKeydown);
      window.addEventListener("scroll", this.onViewportChange, { passive: true });
      window.addEventListener("resize", this.onViewportChange);
      this.addEventListener("click", this.onSectionClick);
      this.addComponent?.addEventListener("multi-add-cart:success", this.onCartSuccess);
      this.addComponent?.addEventListener("multi-add-cart:error", this.onCartError);
      this.updateVisibility();
      this.render();
    }

    disconnectedCallback() {
      document.removeEventListener("click", this.onDocumentClick, true);
      document.removeEventListener("keydown", this.onKeydown);
      window.removeEventListener("scroll", this.onViewportChange);
      window.removeEventListener("resize", this.onViewportChange);
      if (this.visibilityFrame !== null) cancelAnimationFrame(this.visibilityFrame);
      this.visibilityFrame = null;
      this.removeEventListener("click", this.onSectionClick);
      this.addComponent?.removeEventListener("multi-add-cart:success", this.onCartSuccess);
      this.addComponent?.removeEventListener("multi-add-cart:error", this.onCartError);
    }

    onViewportChange() {
      if (this.visibilityFrame !== null) return;

      this.visibilityFrame = requestAnimationFrame(() => {
        this.visibilityFrame = null;
        this.updateVisibility();
      });
    }

    updateVisibility() {
      const triggerLine = window.innerHeight / 3;
      const startReached = !this.visibilityStart || this.visibilityStart.getBoundingClientRect().top <= triggerLine;
      const endNotReached = !this.visibilityEnd || this.visibilityEnd.getBoundingClientRect().top > triggerLine;
      const shouldShow = startReached && endNotReached;

      this.classList.toggle("is-scroll-visible", shouldShow);
      this.setAttribute("aria-hidden", String(!shouldShow));

      if (!shouldShow && this.modal && !this.modal.hidden) this.closeModal();
    }

    findVisibilityBoundary(className) {
      const normalizedClassName = String(className || "")
        .trim()
        .replace(/^\./, "");

      if (!normalizedClassName) return null;
      return document.getElementsByClassName(normalizedClassName)[0] || null;
    }

    async onDocumentClick(event) {
      const button = event.target.closest?.("multi-add-cart-button");
      if (!button || !document.body.contains(button) || this.contains(button)) return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      const products = this.parseProducts(button.getAttribute("data-products"));
      if (!products.length) {
        this.showError("Unable to identify the selected product.");
        return;
      }

      try {
        this.clearError();
        const card = button.closest(".p-sale-product-card");
        const resolvedItems = await Promise.all(
          products.map((product) => this.resolveProduct(product, products.length === 1 ? card : null, button)),
        );

        resolvedItems.forEach((item) => {
          const existing = this.items.get(item.variantId);
          if (existing) {
            existing.quantity += item.quantity;
            existing.price = item.price;
            existing.compareAtPrice = item.compareAtPrice;
            existing.title = item.title || existing.title;
            existing.image = item.image || existing.image;
          } else {
            this.items.set(item.variantId, item);
          }
        });
        this.render();
        button.closest("modal-dialog, p-sale-product-modal")?.hide?.();
      } catch (error) {
        console.error("Unable to add the product to Mix & Match", error);
        this.showError(error.message || "Unable to select this product. Please try again.");
      }
    }

    onSectionClick(event) {
      if (event.target.closest("[data-add-button]")) {
        if (!this.totalQuantity || this.isSubmitting) return;
        this.isSubmitting = true;
        this.clearError();
        this.addButton.disabled = true;
        return;
      }

      const viewButton = event.target.closest("[data-view-button]");
      if (viewButton) {
        if (!this.totalQuantity) return;
        if (this.isMobile()) {
          this.classList.toggle("is-expanded");
          viewButton.setAttribute("aria-expanded", String(this.classList.contains("is-expanded")));
          this.renderAddLabel();
        } else {
          this.openModal(viewButton);
        }
        return;
      }

      const modalTrigger = event.target.closest("[data-open-modal]");
      if (modalTrigger) {
        if (this.totalQuantity) this.openModal(modalTrigger);
        return;
      }

      if (event.target.closest("[data-close-modal]")) {
        this.closeModal();
        return;
      }

      const quantityButton = event.target.closest("[data-quantity-action]");
      if (!quantityButton) return;

      const variantId = Number(quantityButton.dataset.variantId);
      const item = this.items.get(variantId);
      if (!item) return;

      if (quantityButton.dataset.quantityAction === "increase") {
        item.quantity += 1;
      } else if (item.quantity > 1) {
        item.quantity -= 1;
      } else {
        this.items.delete(variantId);
      }

      this.render();
      if (!this.totalQuantity) this.closeModal();
    }

    onKeydown(event) {
      if (this.modal?.hidden) return;
      if (event.key === "Escape") {
        this.closeModal();
        return;
      }
      if (event.key !== "Tab") return;

      const focusable = Array.from(
        this.dialog.querySelectorAll(
          'button:not([disabled]), [href], input:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      );
      if (!focusable.length) {
        event.preventDefault();
        this.dialog.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (document.activeElement === this.dialog) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
      } else if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    onCartSuccess() {
      this.isSubmitting = false;
      this.items.clear();
      this.classList.remove("is-expanded");
      this.querySelector("[data-view-button]")?.setAttribute("aria-expanded", "false");
      this.render();
    }

    onCartError(event) {
      this.isSubmitting = false;
      this.render();
      this.showError(event.detail?.error?.message || "Unable to add these items to cart. Please try again.");
    }

    parseProducts(value) {
      try {
        return JSON.parse(value || "[]")
          .map((product) => ({
            variantId: Number(product.variantId || product.id),
            quantity: Math.max(1, Number.parseInt(product.quantity || 1, 10)),
            handle: String(product.handle || product.productHandle || "").trim(),
          }))
          .filter((product) => Number.isFinite(product.variantId) && product.variantId > 0);
      } catch (error) {
        console.error("Invalid multi-add-cart-button data-products", error);
        return [];
      }
    }

    async resolveProduct(product, card, button) {
      const cardItem = card ? this.readCardProduct(card, product) : null;
      const handle = product.handle || cardItem?.handle || this.findProductHandle(button, product.variantId);
      const productData = await this.fetchProduct(handle);
      const variant = productData.variants?.find((item) => Number(item.id) === product.variantId);

      if (!variant) throw new Error(`Unable to find variant ${product.variantId} in product ${handle}.`);

      const image = this.getImageUrl(variant.featured_image) || this.getImageUrl(productData.featured_image);

      if (cardItem) {
        return {
          ...cardItem,
          image,
        };
      }

      const price = Number(variant.price) || 0;
      const compareAtPrice = Number(variant.compare_at_price) || price;

      return {
        variantId: product.variantId,
        quantity: product.quantity,
        title: variant.name || productData.title || variant.title || `Variant ${product.variantId}`,
        image,
        price,
        compareAtPrice: Math.max(compareAtPrice, price),
      };
    }

    readCardProduct(card, product) {
      let productInfo = {};
      try {
        productInfo = JSON.parse(card.getAttribute("data-product-info") || "{}");
      } catch (error) {
        console.warn("Invalid p-sale-product-card data-product-info", error);
      }

      const variant = productInfo.variants?.find((item) => Number(item.variantId) === product.variantId);
      const visiblePrice = this.parseMoney(card.querySelector(".on-sale")?.textContent);
      const visibleCompareAt = this.parseMoney(card.querySelector(".compare-at")?.textContent);
      const price = visiblePrice ?? Number(variant?.price);
      if (!Number.isFinite(price)) return null;

      const compareAtPrice = visibleCompareAt ?? (Number(variant?.compareAtPrice) || price);
      return {
        variantId: product.variantId,
        quantity: product.quantity,
        handle: this.findHandleInElement(card, product.variantId) || productInfo.product || card.dataset.productHandle,
        title:
          card.querySelector(".p-sale-product-card-product-title")?.textContent.trim() ||
          productInfo.product ||
          "Product",
        price,
        compareAtPrice: Math.max(compareAtPrice, price),
      };
    }

    parseMoney(value) {
      if (!value?.trim()) return null;
      const normalized = value.replace(/[^0-9.,-]/g, "").replace(/,/g, "");
      const amount = Number.parseFloat(normalized);
      return Number.isFinite(amount) ? Math.round(amount * 100) : null;
    }

    getImageUrl(image) {
      if (!image) return "";
      if (typeof image === "string") return image;
      return image.src || image.url || "";
    }

    findProductHandle(button, variantId) {
      const localScope = button?.closest("section, [id^='shopify-section-']") || document;
      const matchedHandle =
        this.findHandleInElement(localScope, variantId) || this.findHandleInElement(document, variantId);
      return matchedHandle || button?.closest("[data-product-handle]")?.dataset.productHandle || "";
    }

    findHandleInElement(element, variantId) {
      if (!element) return "";

      for (const option of element.querySelectorAll("[data-variant]")) {
        try {
          const variant = JSON.parse(option.dataset.variant || "{}");
          if (Number(variant.id || variant.variantId) === variantId && variant.handle) return variant.handle;
        } catch (error) {
          console.warn("Invalid variant data while resolving product handle", error);
        }
      }

      for (const productElement of element.querySelectorAll("[data-product-info]")) {
        try {
          const productInfo = JSON.parse(productElement.dataset.productInfo || "{}");
          const hasVariant = productInfo.variants?.some(
            (variant) => Number(variant.variantId || variant.id) === variantId,
          );
          if (hasVariant && productInfo.product) return productInfo.product;
        } catch (error) {
          console.warn("Invalid product data while resolving product handle", error);
        }
      }

      return "";
    }

    async fetchProduct(handle) {
      const normalizedHandle = String(handle || "").trim();
      if (!normalizedHandle) throw new Error("Unable to identify the selected product handle.");

      if (!this.productCache.has(normalizedHandle)) {
        const root = window.Shopify?.routes?.root || "/";
        const request = fetch(`${root}products/${encodeURIComponent(normalizedHandle)}.js`)
          .then(async (response) => {
            if (!response.ok) throw new Error(`Unable to load product ${normalizedHandle}.`);
            return response.json();
          })
          .catch((error) => {
            this.productCache.delete(normalizedHandle);
            throw error;
          });
        this.productCache.set(normalizedHandle, request);
      }
      return this.productCache.get(normalizedHandle);
    }

    parseDiscountRules(value) {
      return String(value || "")
        .split(",")
        .map((entry) => {
          const [quantityPart, factorPart] = entry.split(":");
          const factor = Number.parseFloat(factorPart);
          if (!quantityPart || !Number.isFinite(factor)) return null;

          if (quantityPart.includes("+")) {
            return { min: Number.parseInt(quantityPart, 10), max: Infinity, factor };
          }
          if (quantityPart.includes("-")) {
            const [min, max] = quantityPart.split("-").map((part) => Number.parseInt(part, 10));
            return { min, max, factor };
          }
          const quantity = Number.parseInt(quantityPart, 10);
          return { min: quantity, max: quantity, factor };
        })
        .filter((rule) => rule && Number.isFinite(rule.min) && (Number.isFinite(rule.max) || rule.max === Infinity))
        .sort((a, b) => a.min - b.min);
    }

    get totalQuantity() {
      return Array.from(this.items.values()).reduce((sum, item) => sum + item.quantity, 0);
    }

    get discountFactor() {
      let factor = 1;
      this.discountRules.forEach((rule) => {
        if (this.totalQuantity >= rule.min && this.totalQuantity <= rule.max) factor = rule.factor;
      });
      return factor;
    }

    get totals() {
      const subtotal = Array.from(this.items.values()).reduce((sum, item) => sum + item.price * item.quantity, 0);
      const compareAt = Array.from(this.items.values()).reduce(
        (sum, item) => sum + item.compareAtPrice * item.quantity,
        0,
      );
      const total = Math.round(subtotal * this.discountFactor);
      return { total, compareAt, save: Math.max(0, compareAt - total) };
    }

    render() {
      const quantity = this.totalQuantity;
      const totals = this.totals;
      const progressCopy = this.querySelector("[data-progress-copy]");
      const count = this.querySelector("[data-count]");
      const progress = this.querySelector("[data-progress-bar]");
      const products = Array.from(this.items.values()).map((item) => ({
        variantId: item.variantId,
        quantity: item.quantity,
      }));

      progressCopy.textContent =
        quantity === 0
          ? this.dataset.emptyMessage
          : quantity === 1
            ? this.dataset.oneItemMessage
            : quantity === 2
              ? this.dataset.twoItemMessage
              : this.dataset.unlockedMessage;
      count.innerHTML = `<strong>${quantity}</strong> Items Selected`;
      progress.style.width = `${quantity === 0 ? 0 : quantity === 1 ? 33 : quantity === 2 ? 66 : 100}%`;
      this.querySelector("[data-total]").textContent = this.formatMoney(totals.total);
      this.querySelector("[data-compare]").textContent = this.formatMoney(totals.compareAt);
      this.querySelector("[data-save]").textContent = this.formatMoney(totals.save);
      this.addComponent?.setAttribute("data-products", JSON.stringify(products));
      this.addButton.disabled = !quantity || this.isSubmitting;
      this.querySelector("[data-view-button]").disabled = !quantity;
      this.querySelector("[data-open-modal]").disabled = !quantity;
      this.classList.toggle("is-empty", !quantity);

      this.renderPreviews();
      this.renderModalItems();
      this.renderAddLabel();

      if (!quantity) {
        this.classList.remove("is-expanded");
        this.querySelector("[data-view-button]")?.setAttribute("aria-expanded", "false");
      }
    }

    renderPreviews() {
      const list = this.querySelector("[data-preview-list]");
      const previewItems = Array.from(this.items.values()).flatMap((item) =>
        Array.from({ length: item.quantity }, () => item),
      );
      list.replaceChildren(...previewItems.map((item) => this.createPreview(item)));
    }

    createPreview(item) {
      const preview = document.createElement("div");
      preview.className = "labor-day-mix-match__preview";

      if (item.image) {
        const image = document.createElement("img");
        image.src = item.image;
        image.alt = item.title;
        image.loading = "lazy";
        preview.append(image);
      }

      const removeButton = document.createElement("button");
      removeButton.type = "button";
      removeButton.className = "labor-day-mix-match__preview-remove";
      removeButton.dataset.quantityAction = "decrease";
      removeButton.dataset.variantId = item.variantId;
      removeButton.setAttribute("aria-label", `Remove one ${item.title}`);
      removeButton.innerHTML = `
        <svg aria-hidden="true" width="17" height="17" viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="8.33709" cy="8.17302" r="4.37615" fill="white"/>
          <path d="M8.40631 7.83894L6.23554 5.59071C6.06412 5.41434 5.78391 5.41104 5.60755 5.58246C5.52184 5.66323 5.47239 5.77531 5.47074 5.89399C5.46745 6.01266 5.5136 6.12639 5.59436 6.21045L7.76513 8.45869L5.51689 10.6295C5.34053 10.8009 5.33723 11.0811 5.50865 11.2574C5.58942 11.3432 5.7015 11.3926 5.82018 11.3943C5.93885 11.3976 6.05258 11.3514 6.13664 11.2706L8.38488 9.09987L10.5556 11.3481C10.7271 11.5245 11.0073 11.5278 11.1836 11.3563C11.2693 11.2756 11.3188 11.1635 11.3204 11.0448C11.3237 10.9261 11.2776 10.8124 11.1968 10.7284L9.02606 8.48012L11.2743 6.30935C11.4507 6.13793 11.454 5.85773 11.2825 5.68136C11.2018 5.59565 11.0897 5.5462 10.971 5.54455C10.8523 5.54126 10.7386 5.58741 10.6545 5.66817L8.40631 7.83894ZM3.63128 13.0689C1.06493 10.4102 1.1391 6.17584 3.7961 3.60784C6.45476 1.04149 10.6892 1.11566 13.2572 3.77267C15.8235 6.43132 15.751 10.6657 13.0923 13.2337C10.4337 15.8001 6.19928 15.7276 3.63128 13.0689Z" fill="#333333"/>
        </svg>
      `;
      preview.append(removeButton);
      return preview;
    }

    renderModalItems() {
      const container = this.querySelector("[data-modal-items]");
      container.replaceChildren(...Array.from(this.items.values()).map((item) => this.createModalItem(item)));
    }

    createModalItem(item) {
      const row = document.createElement("article");
      row.className = "labor-day-mix-match__item";

      const media = document.createElement("div");
      media.className = "labor-day-mix-match__item-media";
      if (item.image) {
        const image = document.createElement("img");
        image.className = "labor-day-mix-match__item-image";
        image.src = item.image;
        image.alt = item.title;
        media.append(image);
      }

      const info = document.createElement("div");
      const title = document.createElement("div");
      title.className = "labor-day-mix-match__item-title";
      title.textContent = item.title;

      const price = document.createElement("p");
      price.className = "labor-day-mix-match__item-price";
      const currentPrice = document.createElement("strong");
      currentPrice.textContent = this.formatMoney(item.price);
      const compareAtPrice = document.createElement("s");
      compareAtPrice.textContent = this.formatMoney(item.compareAtPrice);
      price.append(currentPrice, compareAtPrice);

      const quantity = document.createElement("div");
      quantity.className = "labor-day-mix-match__quantity";
      const decrease = this.createQuantityButton(item, "decrease");
      const output = document.createElement("output");
      output.textContent = item.quantity;
      output.setAttribute("aria-label", `Quantity ${item.quantity}`);
      const increase = this.createQuantityButton(item, "increase");
      quantity.append(decrease, output, increase);
      info.append(title, price, quantity);
      row.append(media, info);
      return row;
    }

    createQuantityButton(item, action) {
      const button = document.createElement("button");
      button.type = "button";
      button.dataset.quantityAction = action;
      button.dataset.variantId = item.variantId;
      if (action === "increase") {
        button.textContent = "+";
        button.setAttribute("aria-label", `Increase ${item.title} quantity`);
      } else if (item.quantity === 1) {
        button.innerHTML = `<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M3.71484 4.12988V18.1706H16.1037V4.12988H3.71484Z" stroke="black" stroke-width="1.72367" stroke-linejoin="round"/><path d="M8.25781 8.25879V13.6273" stroke="black" stroke-width="1.72367" stroke-linecap="round" stroke-linejoin="round"/><path d="M11.5625 8.25879V13.6273" stroke="black" stroke-width="1.72367" stroke-linecap="round" stroke-linejoin="round"/><path d="M1.65234 4.12988H18.1708" stroke="black" stroke-width="1.72367" stroke-linecap="round" stroke-linejoin="round"/><path d="M6.60547 4.12914L7.9637 1.65137H11.8819L13.2129 4.12914H6.60547Z" stroke="black" stroke-width="1.72367" stroke-linejoin="round"/></svg>`;
        button.setAttribute("aria-label", `Remove ${item.title}`);
      } else {
        button.textContent = "−";
        button.setAttribute("aria-label", `Decrease ${item.title} quantity`);
      }
      return button;
    }

    renderAddLabel() {
      const label = this.querySelector("[data-add-label]");
      if (!label) return;
      label.textContent = this.totalQuantity ? `${this.baseAddLabel} (${this.totalQuantity})` : this.baseAddLabel;
    }

    formatMoney(amount) {
      if (window.theme?.Currency?.formatMoney) {
        return window.theme.Currency.formatMoney(amount, window.theme.strings?.moneyFormat);
      }
      return `$${(amount / 100).toFixed(2)}`;
    }

    isMobile() {
      return window.matchMedia("(max-width: 850px)").matches;
    }

    openModal(trigger) {
      if (!this.modal || !this.totalQuantity) return;
      this.lastFocusedElement = trigger || document.activeElement;
      this.bodyWasOverflowHidden = document.body.classList.contains("overflow-hidden");
      this.modal.hidden = false;
      document.body.classList.add("overflow-hidden");
      requestAnimationFrame(() => this.dialog?.focus());
    }

    closeModal() {
      if (!this.modal || this.modal.hidden) return;
      this.modal.hidden = true;
      if (!this.bodyWasOverflowHidden) document.body.classList.remove("overflow-hidden");
      this.lastFocusedElement?.focus?.();
    }

    clearError() {
      const error = this.querySelector("[data-error]");
      error.hidden = true;
      error.textContent = "";
    }

    showError(message) {
      const error = this.querySelector("[data-error]");
      error.textContent = message;
      error.hidden = false;
    }
  }

  customElements.define("labor-day-2026-mix-match-cart", LaborDayMixMatchCart);
}
